import { Material } from "@/models/material.model";
import {
  IMaterialEvent,
  TraditionalRest,
} from "@/services/rest/traditional.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { defineStore } from "pinia";
import { instance } from "..";
import { useChapterStore } from "./chapter";
import { ELoader, useLoaderStore } from "./loader";
import { useNavigationStore } from "./navigation";
import { useTrailStore } from "./trail";

const store = defineStore({
  id: "material",
  state: () => ({
    materials: [] as Material[],
    currentMaterial: null as Material | null,
  }),
  getters: {
    isImage: (state) => {
      return (
        state.currentMaterial?.file?.mimetype == "png" ||
        state.currentMaterial?.file?.mimetype == "jpg" ||
        state.currentMaterial?.file?.mimetype == "jpeg" ||
        state.currentMaterial?.file?.mimetype == "svg"
      );
    },
    isVideo: (state) => {
      return (
        state.currentMaterial?.file?.mimetype == "mp4" ||
        state.currentMaterial?.file?.mimetype == "x-mpegURL"
      );
    },
    isAudio: (state) => {
      return state.currentMaterial?.file?.mimetype == "mp3";
    },
    isPdf: (state) => {
      return state.currentMaterial?.file?.mimetype == "pdf";
    },
    currentMaterialIndex: (state) => {
      return state.materials.findIndex(
        (material) => material.id === state.currentMaterial?.id
      );
    },
    isLastMaterial(state): boolean {
      return this.currentMaterialIndex === state.materials?.length - 1;
    },
    AllMaterialsDone(): boolean {
      return this.materials.every((e) => e.done);
    },
  },
  actions: {
    getMaterialsByTrailId(trailId: string) {
      const courseId = JSON.parse(String(localStorage.getItem("courseID")));
      const classroomId = localStorage.getItem("classId") ?? undefined;

      TraditionalRest.getMaterialsByTrailId(courseId, trailId, {
        classroomId,
      })
        .then(async (response) => {
          this.materials = response;

          const lastIndex = Math.max(
            useMaterialStore.materials.findIndex((e) => !e.done),
            0
          );
          await useMaterialStore.selectMaterial(lastIndex);
        })
        .finally(() => useLoaderStore.stop(ELoader.MATERIALS));
    },

    getMaterialById(learningObjectivesId: string, materialId: string) {
      const classroomId = localStorage.getItem("classId") ?? undefined;

      useLoaderStore.start(ELoader.MATERIAL);
      TraditionalRest.getMaterialById(learningObjectivesId, materialId, {
        classroomId,
      })
        .then((response) => {
          response.file.url = new GenericsUtils().getSecureUrl(
            response?.file?.url
          );
          response.file.mimetype = new GenericsUtils().getMimeType(
            response?.file?.mimetype
          );

          this.currentMaterial = {
            ...response,
            done: this.materials[this.currentMaterialIndex]?.done,
          };
        })
        .finally(() => useLoaderStore.stop(ELoader.MATERIAL));
    },

    selectMaterial(index: number) {
      if (!this.materials[index]) return;
      if (this.currentMaterial?.id === this.materials[index].id) return;

      this.getMaterialById(
        useTrailStore.currentTrail?.id!,
        this.materials[index].id
      );
    },

    setMaterialDoneById(materialId: string) {
      const material = this.materials.find((m) => m.id === materialId);
      if (material) material.done = true;
    },

    materialNext() {
      if (!this.isVideo) this.materialEvent();
      const advance = !this.isLastMaterial;
      if (advance) this.selectMaterial(this.currentMaterialIndex + 1);
      return advance;
    },

    materialPrev() {
      const goBack = this.currentMaterialIndex > 0;
      if (goBack) this.selectMaterial(this.currentMaterialIndex - 1);
      return goBack;
    },

    async materialSkipHandler() {
      if (this.isLastMaterial) await useNavigationStore.next();
    },

    materialEvent() {
      const courseId = JSON.parse(String(localStorage.getItem("courseID")));
      const classroomId = localStorage.getItem("classId") ?? undefined;

      const currentMaterial = this.materials[this.currentMaterialIndex];

      if (!currentMaterial?.done) {
        const data: IMaterialEvent = {
          clientTime: new Date(),
          concluded: true,
          learningObjectiveId: currentMaterial?.learningObjectiveId!,
          materialId: currentMaterial?.id!,
          skipped: currentMaterial?.skipped!,
          stoppedAt: currentMaterial?.stoppedAt ?? 0,
        };

        TraditionalRest.materialEvent(courseId, data, {
          classroomId,
        }).then(async () => {
          useLoaderStore.stop(ELoader.TRAILS);
          this.setMaterialDoneById(currentMaterial?.id!);
          await useTrailStore.getTrailsByChapterId(
            useChapterStore.currentChapter?.id!
          );
          await useTrailStore.trailEvent();
          await this.materialSkipHandler();
        });
      }
    },
  },
});

export const useMaterialStore = store(instance);
